import { ConfigService } from '@bo-client/config/config.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import {
  ActionWithMoreInfoModel,
  DataForCustomFormModel,
  SecondActionTypes
} from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RequestModel } from '@bo-client/shared/models/request.model';
import { UserDto } from '@bo-client/shared/models/userDto';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { RequestFormBase } from '@bo-client/shared/base-components/request-form.base';
import { uri } from '@bo-client/shared/constants/uri';
import { ConfimWithCommentModalComponent } from '@bo-client/activation-management/commissioning/forms/components/modal-validate-commissioning/confim-with-comment-modal.component';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { ToastrService } from 'ngx-toastr';
import { RequestApprovalDto } from '@bo-client/activation-management/grid-registration/model/RequestApprovalDto';
import { CustomFormModalComponent } from '@bo-client/activation-management/shared/components/custom-form-modal/custom-form-modal.component';
import { ContactDataDto } from '@bo-client/shared/models/contactDataDto';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
// tslint:disable-next-line:max-line-length
import { GenericConfirmationModalComponent } from '@bo-client/shared/modals/generic-confirmation-modal/generic-confirmation-modal.component';

export abstract class RequestPageBase<T extends RequestModel> {
  public title: any;
  public form: RequestFormBase<T>;
  protected saveUrl: string = uri.cmGridRegistration;

  public user: UserDto = null;
  public errorFormList: ShowErrorModel[] = [];
  public actions: ActionWithMoreInfoModel[];
  public model: T = null;
  public createdByCustomer: ContactDataDto = null;

  protected compUnsub$ = new Subject<void>();

  constructor(
    protected configService: ConfigService,
    protected dataShareService: DataShareService,
    protected authorizationService: AuthorizationService,
    public matDialog: MatDialog,
    protected httpService: HttpHapService,
    protected toastService: ToastrService
  ) {}

  onFormErrorsChanged(formErrorChangedEvent: ShowErrorModel[]) {
    this.errorFormList = formErrorChangedEvent;
  }

  get isEditor() {
    try {
      return !!this.user.roles.find((x) => x.name === 'ROLE_EDITOR_BASE');
    } catch (e) {
      return false;
    }
  }

  confirmActionWithComment(actionInfo: ActionWithMoreInfoModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'commission-request-activity';
    dialogConfig.maxHeight = '100%';
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = actionInfo;

    this.matDialog
      .open(ConfimWithCommentModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsub$))
      .subscribe((res) => {
        if (res) {
          this.saveAndActionPlusMail(actionInfo);
        }
      });
  }

  confirmAction(actionInfo: ActionWithMoreInfoModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'confirm-action';
    dialogConfig.maxHeight = MatDialogSize.maxHeightLogin;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      title: actionInfo.actionName,
      message: 'Bitte bestätigen Sie, dass Sie die Aktion ausführen möchten',
      confirmText: 'Bestätigen',
      cancelText: 'Abbrechen'
    };
    this.matDialog
      .open(GenericConfirmationModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsub$))
      .subscribe((res) => {
        if (res) {
          this.saveAndActionPlusMail(actionInfo);
        }
      });
  }

  customAction(actionInfo: ActionWithMoreInfoModel) {
    const dataForCustomForm: DataForCustomFormModel<unknown> = new DataForCustomFormModel();
    dataForCustomForm.actionInfo = actionInfo;
    dataForCustomForm.model = this.model;
    this.hookCustomAction(dataForCustomForm);
  }

  protected hookCustomAction(dataForCustomForm: DataForCustomFormModel<unknown>) {}

  /*
   * Das Custom Form bringt seine eigene Validierung mit.
   * @param actionInfo
   */
  customFormWithCustomActionModal(actionInfo: ActionWithMoreInfoModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'custom-form-with-custom-action';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.largeWidth;
    dialogConfig.data = {
      actionInfo: actionInfo,
      model: this.model /// TODO: clarify if we want to get the data from the main form in that moment => otherwise it is lost
    };

    /**
     * Ein dialog wird geöffnet mit dem Custom Form
     */
    this.matDialog
      .open(CustomFormModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsub$))
      .subscribe((customFormResponse) => {
        if (customFormResponse) {
          console.log('handling dialog response', customFormResponse);
          // eslint-disable-next-line no-prototype-builtins
          /**
           * This is not intuitive
           */
          if (customFormResponse.hasOwnProperty('additionalData')) {
            actionInfo.additionalData = customFormResponse.additionalData;
            actionInfo.comment = customFormResponse.comment;
          } else {
            // Todo: Warum sollte dann das Model überschrieben werden??
            //  Das sollte vielleicht geändert werden. Ohne das zu wissen, dauert das Debuggen von den Actions extrem lange
            this.model = customFormResponse;
          }
          switch (actionInfo.secondAction) {
            case SecondActionTypes.saveModel:
              this.save(actionInfo, this.model);
              return;
            case SecondActionTypes.saveModelAndActionPlusMail:
              this.saveAndActionPlusMail(actionInfo, this.model);
              return;
          }
        }
      });
  }

  saveAndActionPlusMail(actionData: ActionWithMoreInfoModel, model: T = null) {
    console.log('calling save with model', model);
    // in case we don't pass a model we want to get the data from the main form;
    if (!model) {
      model = this.form.getModelFromForm();
    }

    // in case we want to customize the actionData with the current Model
    if (actionData.preActionHook) {
      actionData.preActionHook(actionData, model);
    }

    this.httpService
      .putOrPost(model, this.saveUrl)
      .pipe(takeUntil(this.compUnsub$))
      .subscribe(
        (result) => {
          this.toastService.success('Speichern erfolgreich');

          this.form.updateFormWithModel(result);

          const requestApprovalDto: RequestApprovalDto = {
            id: result.id,
            subject: actionData.subject,
            comment: actionData.comment
          };

          if (actionData.additionalData) {
            requestApprovalDto.additionalData = actionData.additionalData;
          }

          this.actionPlusMail(requestApprovalDto, actionData);
        },
        (error) => {
          this.displayErrorMessage(error, actionData.failureMessage);
        }
      );
  }

  actionPlusMail(gridRegistrationDto: RequestApprovalDto, actionInfo: ActionWithMoreInfoModel) {
    this.httpService
      .post(gridRegistrationDto, actionInfo.actionUrl)
      .pipe(takeUntil(this.compUnsub$))
      .subscribe(
        (result) => {
          this.toastService.success(actionInfo.successMessage);
          this.updateMainForm(result);
        },
        (error) => {
          this.displayErrorMessage(error, actionInfo.failureMessage);
        }
      );
  }

  save(actionInfo: ActionWithMoreInfoModel, model: T = null) {
    console.log('calling save with model', model);
    // we have to test for actionWithMoreINofmodel since for JS Reasons it can be called in a different way
    if (!model || !(model instanceof RequestModel)) {
      console.log('model wasnt an instance of RequestMOdel');
      model = this.form.getModelFromForm();
    }

    this.httpService
      .putOrPost(model, this.saveUrl)
      .pipe(takeUntil(this.compUnsub$))
      .subscribe(
        (result) => {
          this.toastService.success('Speichern erfolgreich');
          this.updateMainForm(result);
        },
        (error) => {
          this.displayErrorMessage(error, 'Beim Speichern ist ein Fehler aufgetreten');
        }
      );
  }

  /**
   * Todo: handle form validation elsewhere. this is too generic.
   * @param action
   */
  executeAction(action: ActionWithMoreInfoModel) {
    if (action.validateForm) {
      if (!this.form.isValid()) {
        this.toastService.error(
          this.form
            .getAllValidationErrors()
            .map((errorModel) => ShowErrorModel.toReadableString(errorModel))
            .join('<br/>'),
          'Bitte überprüfen Sie Ihre Eingaben für folgende Felder:',
          {
            enableHtml: true
          }
        );
        // log all the validation errors for all nested fields
        return;
      }
    }
    // todo make this saver.... currently we call the function by "name"
    // so the function needs to be implemented on the
    console.log('Executing action', this, action, action.execute);
    this[action.execute](action);
  }

  displayErrorMessage(error: any, defaultMessage: string) {
    if (error?.error?.message) {
      this.toastService.error(error.error.message);
    } else {
      this.toastService.error(defaultMessage);
    }
  }

  protected updateMainForm(result: T) {
    this.model = result;
    this.form.updateFormWithModel(result);
  }
}
